import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, Skeleton } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import homebg from '../../Assets/homebg.png';
import BottomNavigation from '../BottomNavigation/BottomNavigation';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import config from '../../Config';

export default function CheckinScreen() {
  const navigate = useNavigate();
  const { reservationID } = useParams();
  const [booking, setBooking] = useState(null);
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reservationID) {
      setError('No reservation ID provided in URL');
      setLoading(false);
      return;
    }

    const fetchBooking = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${config.apiBaseUrl}/guest/user-Hotel-reservation/${reservationID}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error('Failed to fetch booking data');

        const data = await response.json();
        setBooking(data.booking);
        setPropertyData(data.propertyData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [reservationID]);

  const handleCheckIn = async () => {
    if (!reservationID || !booking?.propertyID) {
      setError('Missing reservation or property information.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      Swal.fire({
        title: 'Checking you in...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await fetch(`${config.apiBaseUrl}/guest/check-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reservationId: reservationID,
          propertyID: booking.propertyID,
          estimatedArrivalTime: null,
          status: 'checked_in',
        }),
      });

      const resData = await response.json();

      Swal.close();

      if (response.ok && resData.success) {
        setBooking((prev) => ({ ...prev, status: 'checked_in' }));

        Swal.fire({
          icon: 'success',
          title: 'Checked In!',
          text: resData.message || 'You have successfully checked in.',
          timer: 1500,
          showConfirmButton: false,
        });

        setTimeout(() => navigate(`/rooms/${reservationID}`), 1500);
      } else {
        throw new Error(resData.message || 'Check-in failed');
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Check-in Failed',
        text: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '400px',
          margin: '0 auto',
          position: 'relative',
          px: 3,
          pt: 2,
        }}
      >
        <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2, mx: 'auto' }} />
        <Container maxWidth="sm" sx={{ flex: 1, px: 0, pb: 12 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            sx={{ borderRadius: '20px', mb: 3 }}
          />
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Skeleton variant="text" width="60%" height={28} sx={{ mx: 'auto', mb: 1 }} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mx: 'auto' }} />
          </Box>
          <Card
            sx={{
              borderRadius: '16px',
              mb: 4,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="text" width="50%" height={24} sx={{ mb: 3 }} />
              <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="70%" height={20} />
            </CardContent>
          </Card>
          <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: '28px' }} />
        </Container>
        <BottomNavigation />
      </Box>
    );
  }

  if (error) {
    return <Typography sx={{ p: 4, textAlign: 'center', color: 'red' }}>{error}</Typography>;
  }

  const dateOptions = { weekday: 'short', day: 'numeric', month: 'short' };
  const guestName = booking?.guestName || 'Guest';
  const roomsBooked = booking?.assigned?.length || 0;
  const startDate = booking?.startDate
    ? new Date(booking.startDate).toLocaleDateString('en-US', dateOptions)
    : '';
  const endDate = booking?.endDate
    ? new Date(booking.endDate).toLocaleDateString('en-US', dateOptions)
    : '';

  let showCheckInButton = false;
  if (booking?.status === 'confirmed' && booking?.startDate) {
    const today = new Date();
    const checkInDate = new Date(booking.startDate);

    today.setHours(0, 0, 0, 0);
    checkInDate.setHours(0, 0, 0, 0);

    const diffDays = Math.floor((checkInDate - today) / (1000 * 60 * 60 * 24));
    showCheckInButton = diffDays === 0 || diffDays === 1;
  }

  const hotelName = propertyData?.propertyName || 'Hotel Name';
  const hotelCity = propertyData?.propertyAddress?.propertyCity || '';
  const hotelImage = propertyData?.propertyImage?.[0]?.image || homebg;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '400px',
        margin: '0 auto',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          mt: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#000', fontSize: '20px' }}>
          Check In
        </Typography>
      </Box>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ flex: 1, px: 3, pb: 12 }}>
        <Card
          sx={{
            borderRadius: '20px',
            mb: 3,
            overflow: 'hidden',
            position: 'relative',
            backgroundImage: `url(${hotelImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: '200px',
          }}
        />

        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: '24px',
              color: '#000',
              mb: 1,
            }}
          >
            {hotelName}
          </Typography>
          {hotelCity && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <LocationOnIcon sx={{ color: '#e53e3e', fontSize: 16 }} />
              <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                {hotelCity}
              </Typography>
            </Box>
          )}
        </Box>

        <Card
          sx={{
            borderRadius: '16px',
            mb: 4,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '18px',
                color: '#000',
                mb: 3,
              }}
            >
              Booking Details
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CalendarTodayIcon sx={{ color: '#666', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                  Dates
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {startDate} - {endDate}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PeopleIcon sx={{ color: '#666', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                  Rooms Booked
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {roomsBooked} {roomsBooked > 1 ? 'rooms' : 'room'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PersonIcon sx={{ color: '#666', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                  Booking For
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {guestName}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {booking?.status === 'checked_in' ? (
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate(`/rooms/${reservationID}`)}
            sx={{
              backgroundColor: '#2e7d7b',
              color: 'white',
              py: 2.2,
              borderRadius: '28px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              height: '56px',
              mb: 2,
              '&:hover': { backgroundColor: '#1e5a58' },
            }}
          >
            View Room
          </Button>
        ) : booking?.status === 'confirmed' && showCheckInButton ? (
          <Button
            fullWidth
            variant="contained"
            onClick={handleCheckIn}
            sx={{
              backgroundColor: '#2e7d7b',
              color: 'white',
              py: 2.2,
              borderRadius: '28px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              height: '56px',
              mb: 2,
              '&:hover': { backgroundColor: '#1e5a58' },
            }}
          >
            Check In
          </Button>
        ) : booking?.status === 'checked_out' ? (
          <Button
            fullWidth
            variant="contained"
            disabled
            sx={{
              backgroundColor: '#ccc',
              color: '#666',
              py: 2.2,
              borderRadius: '28px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              height: '56px',
              mb: 2,
            }}
          >
            Checked Out
          </Button>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(`/rooms/${reservationID}`)}
            sx={{
              color: '#2e7d7b',
              borderColor: '#2e7d7b',
              py: 2.2,
              borderRadius: '28px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              height: '56px',
              mb: 2,
              '&:hover': {
                borderColor: '#1e5a58',
                backgroundColor: 'rgba(46,125,123,0.04)',
              },
            }}
          >
            View Room
          </Button>
        )}
      </Container>

      <BottomNavigation />
    </Box>
  );
}
